import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import TaskSubNav from '../components/TaskSubNav';
import { DSBackLink, DSButton, DSIcon, DSBadge } from '../design-system/primitives';
import StatusBadge from '../components/StatusBadge';
import './taskManagementShared.css';

function MaintenanceWalkthrough() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiService.getWalkthroughChecklist()
      .then((res) => {
        const cats = res.data?.categories || [];
        setCategories(cats);
        const preChecked = new Set();
        cats.forEach((cat) => cat.templates.forEach((t) => {
          if (t.hasOpenInstance) {
            preChecked.add(t.id);
          }
        }));
        setSelected(preChecked);
      })
      .catch((err) => setError(err.response?.data?.error || 'Unable to load walkthrough'))
      .finally(() => setLoading(false));
  }, []);

  const toggleTemplate = (id, hasOpenInstance) => {
    if (hasOpenInstance) {
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectableIds = (templates) => templates.filter((t) => !t.hasOpenInstance).map((t) => t.id);

  const isAllSelected = (templates) => {
    const ids = selectableIds(templates);
    return ids.length > 0 && ids.every((id) => selected.has(id));
  };

  const toggleAll = (templates) => {
    const ids = selectableIds(templates);
    const allSelected = isAllSelected(templates);
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const selectedCount = Array.from(selected).length;

  const handleSubmit = async () => {
    const newSelections = Array.from(selected).filter((id) => {
      return categories.some((cat) => cat.templates.some((t) => t.id === id && !t.hasOpenInstance));
    });
    if (newSelections.length === 0) {
      navigate('/tasks', { replace: true });
      return;
    }
    try {
      setSubmitting(true);
      await apiService.submitWalkthrough(newSelections);
      navigate('/tasks', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit walkthrough');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading-inline">Loading...</div>;
  }

  return (
    <div className="tm-page">
      <DSBackLink onClick={() => navigate(-1)}>Back</DSBackLink>
      <div className="tm-section">
        <h1>Maintenance Walkthrough</h1>
        <p>Check which tasks are needed right now. Submitting spawns instances for selected items.</p>
        {error && <div className="error-message">{error}</div>}

        {categories.length === 0 ? (
          <div className="tm-empty-state"><h2>No on-demand templates available</h2></div>
        ) : (
          categories.map((cat) => (
            <div key={cat.categoryId || 'uncategorized'} className="tm-walkthrough-category">
              <div className="tm-section-header-row">
                <h3>{cat.categoryName}</h3>
                <button type="button" className="tm-link-btn" onClick={() => toggleAll(cat.templates)}>
                  {isAllSelected(cat.templates) ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              {cat.templates.map((t) => (
                <label key={t.id} className={`tm-walkthrough-item ${t.hasOpenInstance ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={selected.has(t.id)}
                    disabled={t.hasOpenInstance}
                    onChange={() => toggleTemplate(t.id, t.hasOpenInstance)}
                  />
                  <span>{t.title}</span>
                  <DSBadge variant="brand" size="sm" leadingIcon={<DSIcon name="star" />}>{t.pointValue}pt</DSBadge>
                  {t.hasOpenInstance && <StatusBadge variant="success" size="sm" leadingIcon={<DSIcon name="checkCircle" />}>Already open</StatusBadge>}
                </label>
              ))}
            </div>
          ))
        )}

        <DSButton appearance="solid" tone="primary" className="tm-walkthrough-submit" onClick={handleSubmit} disabled={submitting} leadingIcon={<DSIcon name="sparkles" />}>{submitting ? 'Spawning...' : `Spawn ${selectedCount} Task${selectedCount === 1 ? '' : 's'}`}
        </DSButton>
      </div>
      <TaskSubNav />
    </div>
  );
}

export default MaintenanceWalkthrough;
